<script lang="ts">
  import { groupState, onlyType, toggleType, type TypeClassifier, type TypeCount } from '../logic/filefilter';

  interface Props {
    /** Groups present in the scope, with counts over the files the other filters leave. */
    types: TypeCount[];
    /** Selected group IDs and keys (`types=`). */
    selected: string[];
    classifier: TypeClassifier;
    onchange: (selected: string[]) => void;
  }
  let { types, selected, classifier, onchange }: Props = $props();

  /** Groups shown before "more" (selected ones always show). */
  const FIRST = 6;

  let expanded: Record<string, boolean> = $state({});
  let more = $state(false);

  const shown = $derived(more ? types : types.filter((t, i) => i < FIRST || groupState(selected, t.group, classifier) !== 'none'));
  const hidden = $derived(types.length - shown.length);
  /** Selected tokens no chip shows (no such files in this scope). */
  const absent = $derived.by(() => {
    const known = new Set(types.flatMap((t) => [t.group, ...t.keys.map((k) => k.key)]));
    return selected.filter((t) => !known.has(t));
  });

  function isOpen(t: TypeCount): boolean {
    return expanded[t.group] ?? groupState(selected, t.group, classifier) === 'some';
  }

  function pick(e: MouseEvent | KeyboardEvent, token: string) {
    onchange(e.altKey ? onlyType(token) : toggleType(selected, token, classifier));
  }

  // Alt+Enter / Alt+Space: "only this" from the keyboard (a keyboard click carries no modifier everywhere).
  function onkeydown(e: KeyboardEvent, token: string) {
    if (e.altKey && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      onchange(onlyType(token));
    }
  }

  function plural(n: number) {
    return `${n} file${n === 1 ? '' : 's'}`;
  }
</script>

<div class="type-chips" role="group" aria-label="Filter by file type">
  {#each shown as t (t.group)}
    {@const state = groupState(selected, t.group, classifier)}
    {@const open = isOpen(t)}
    <span class="type-group" class:open>
      <button
        class="type-chip"
        class:on={state === 'all'}
        class:some={state === 'some'}
        class:empty={!t.count}
        aria-pressed={state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false'}
        aria-label="{t.name}, {plural(t.count)}"
        title="{t.name}: {plural(t.count)}. Alt-click: only this type."
        onclick={(e) => pick(e, t.group)}
        onkeydown={(e) => onkeydown(e, t.group)}>{t.name}<span class="n" aria-hidden="true">{t.count}</span></button
      >
      {#if t.keys.length > 1}
        <button
          class="type-expand"
          aria-expanded={open}
          aria-label="{open ? 'Hide' : 'Show'} {t.name} extensions"
          title="{open ? 'Hide' : 'Show'} {t.name} extensions"
          onclick={() => (expanded = { ...expanded, [t.group]: !open })}>{open ? '‹' : '›'}</button
        >
      {/if}
      {#if open && t.keys.length > 1}
        {#each t.keys as k (k.key)}
          {@const on = selected.includes(k.key)}
          <button
            class="type-chip key"
            class:on
            class:empty={!k.count}
            aria-pressed={on}
            aria-label="{k.key}, {plural(k.count)}"
            title="{k.key}: {plural(k.count)}. Alt-click: only this."
            onclick={(e) => pick(e, k.key)}
            onkeydown={(e) => onkeydown(e, k.key)}>{k.key}<span class="n" aria-hidden="true">{k.count}</span></button
          >
        {/each}
      {/if}
    </span>
  {/each}
  {#each absent as token (token)}
    <button
      class="type-chip on empty"
      aria-pressed="true"
      aria-label="{classifier.isGroup(token) ? classifier.groupName(token) : token}, no files here"
      title="No such files in this list. Click to remove."
      onclick={() => onchange(selected.filter((t) => t !== token))}>{classifier.isGroup(token) ? classifier.groupName(token) : token}<span class="n" aria-hidden="true">0</span></button
    >
  {/each}
  {#if hidden > 0 || more}
    <button class="type-more" aria-expanded={more} onclick={() => (more = !more)}>{more ? 'Fewer' : `+${hidden} more`}</button>
  {/if}
  {#if selected.length}
    <button class="type-clear" title="Show every file type" onclick={() => onchange([])}>Clear</button>
  {/if}
</div>
